import React, { useMemo, useState } from 'react';
import { useAITTheme, type AITTheme } from '@this/shared/ui/theme';
import type { AiMessagePayload } from '../api/messages';

type PresetPeriodKey = '1_week' | '2_weeks' | 'this_month';

type PeriodChip = {
  key: PresetPeriodKey;
  label: string;
  ctaLabel: string;
};

const PERIOD_CHIPS: PeriodChip[] = [
  { key: '1_week', label: '1週間', ctaLabel: '1週間の予定を確認する' },
  { key: '2_weeks', label: '2週間', ctaLabel: '2週間の予定を確認する' },
  { key: 'this_month', label: '今月', ctaLabel: '今月の予定を確認する' }
];

const DEFAULT_PERIOD: PresetPeriodKey = '2_weeks';

type Props = {
  onSubmit: (payload: AiMessagePayload) => void;
};

const CalendarStarterCard = ({ onSubmit }: Props) => {
  const { theme } = useAITTheme();
  const [selectedPeriod, setSelectedPeriod] = useState<PresetPeriodKey>(DEFAULT_PERIOD);
  const styles = useMemo(() => getStyles(theme), [theme]);

  const selectedChip = PERIOD_CHIPS.find(c => c.key === selectedPeriod) ?? PERIOD_CHIPS[1];

  const periodTextMap: Record<PresetPeriodKey, string> = {
    '1_week': 'Googleカレンダーの今後1週間の予定を見せてください',
    '2_weeks': 'Googleカレンダーの今後2週間の予定を見せてください',
    this_month: 'Googleカレンダーの今月の予定を見せてください'
  };

  const handleSubmit = () => {
    onSubmit({
      text: periodTextMap[selectedPeriod]
    });
  };

  return (
    <div style={styles.card}>
      <div style={styles.title}>カレンダーの予定を確認</div>
      <div style={styles.description}>指定した期間の予定や空いている日を確認できます</div>
      <div style={styles.helperText}>予定を見ながら、次の相談や提案に進めます</div>
      <div style={styles.chips}>
        {PERIOD_CHIPS.map(chip => (
          <button
            key={chip.key}
            type="button"
            style={chip.key === selectedPeriod ? styles.chipSelected : styles.chip}
            onClick={() => setSelectedPeriod(chip.key)}
          >
            {chip.label}
          </button>
        ))}
      </div>
      <button type="button" style={styles.cta} onClick={handleSubmit}>
        {selectedChip.ctaLabel}
      </button>
    </div>
  );
};

const getStyles = (theme: AITTheme) => ({
  card: {
    border: `1px solid ${theme.grayBorderColor}`,
    borderRadius: '12px',
    padding: '20px 24px',
    backgroundColor: '#ffffff',
    maxWidth: '480px',
    width: '100%',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '12px'
  },
  title: {
    fontSize: '15px',
    fontWeight: 600 as const,
    color: theme.textColor ?? '#333333'
  },
  description: {
    fontSize: '13px',
    color: theme.grayTextColor,
    lineHeight: '1.5'
  },
  helperText: {
    fontSize: '12px',
    color: theme.grayTextColor,
    lineHeight: '1.5'
  },
  chips: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap' as const
  },
  chip: {
    fontSize: '13px',
    padding: '6px 14px',
    borderRadius: '16px',
    border: `1px solid ${theme.grayBorderColor}`,
    backgroundColor: '#ffffff',
    color: theme.grayTextColor,
    cursor: 'pointer'
  },
  chipSelected: {
    fontSize: '13px',
    padding: '6px 14px',
    borderRadius: '16px',
    border: `1px solid ${theme.linkColor}`,
    backgroundColor: '#e6f4fb',
    color: theme.linkColor,
    cursor: 'pointer',
    fontWeight: 600 as const
  },
  cta: {
    fontSize: '14px',
    fontWeight: 600 as const,
    padding: '12px 20px',
    borderRadius: '8px',
    border: 'none',
    backgroundColor: theme.linkColor,
    color: '#ffffff',
    cursor: 'pointer',
    alignSelf: 'flex-start' as const
  }
});

export default CalendarStarterCard;
